import Dexie from 'dexie';
import lodashGet from 'lodash/get';
import lodashChunk from 'lodash/chunk';
import db_tables from '@/api/data/db_tables';
import { apis } from '@/config';
import { api } from '@/api/api';
import { getCommonCompareFunc, util } from '@/api/util';
import { AbstractDBInterfaceImpl } from './db_abs';
let hasShowConfim = false;
function getIsVersionError(ex) {
    if (ex && ex.inner && ex.inner.name === 'VersionError' && ex.inner.message && ex.inner.message.indexOf('is less than the existing version')) {
        return true;
    }
    return false;
}
class ActionStore {
    constructor() {
        this.user = undefined;
        this.lock = undefined;
        this.openedSchemas = {};
        this.filterRegistry = new Map();
    }
}
class DexieDbApiImpl extends AbstractDBInterfaceImpl {
    constructor() {
        var _a, _b;
        super();
        this.accountAction = new Map();
        this.LOCK_KEY_FOR_DB = 'dbWrLock';
        this.schemas = {};
        Object.keys(db_tables).forEach(idx => {
            const id = idx;
            if (db_tables[id]) {
                const item = db_tables[id];
                this.schemas[id] = item;
            }
        });
        this.name = apis.dexieDbApi;
        this.systemApi = api.getSystemApi();
        this.storeApi = api.getDataStoreApi();
        this.accountApi = api.requireLogicalApi(apis.accountApiImpl);
        this.dataTrackApi = api.requireLogicalApi(apis.dataTrackerApiImp);
        const currentUser = ((_b = (_a = this.systemApi) === null || _a === void 0 ? void 0 : _a.getCurrentUser()) === null || _b === void 0 ? void 0 : _b.accountMd5) || '';
        if (currentUser) {
            this.accountAction.set(currentUser, new ActionStore());
        }
        this.accountAction.set('global', new ActionStore());
        this.action = new ActionStore();
    }
    close() {
        [...this.accountAction.keys()].forEach(account => {
            Object.keys(this.accountAction.get(account)).forEach(name => {
                this.closeSpecific(name, account);
            });
        });
        [...this.accountAction.values()].forEach(action => {
            var _a;
            (_a = action.lock) === null || _a === void 0 ? void 0 : _a.destroyLock();
        });
    }
    closeSpecific(db, _account = '') {
        var _a;
        let account = this.accountApi.getEmailIdByEmail(_account);
        if ((_a = this.schemas[db]) === null || _a === void 0 ? void 0 : _a.global) {
            account = 'global';
        }
        else if (!account || !account.length) {
            account = lodashGet(this.systemApi.getCurrentUser(), 'accountMd5', '');
        }
        if (typeof account === 'string' && account.includes('@')) {
            account = this.systemApi.md5(account, true);
        }
        if (!this.accountAction.has(account)) {
            return;
        }
        const action = this.accountAction.get(account);
        if (Object.prototype.hasOwnProperty.apply(action.openedSchemas, [db]) && action.openedSchemas[db]) {
            try {
                const dbIns = action.openedSchemas[db];
                if (dbIns.instance.isOpen())
                    dbIns.instance.close();
            }
            catch (e) {
                console.warn(e);
            }
            delete action.openedSchemas[db];
            this.accountAction.set(account, action);
        }
    }
    init() {
        var _a, _b;
        const currentUser = ((_b = (_a = this.systemApi) === null || _a === void 0 ? void 0 : _a.getCurrentUser()) === null || _b === void 0 ? void 0 : _b.accountMd5) || '';
        if (currentUser) {
            this.accountAction.set(currentUser, new ActionStore());
        }
        return this.name;
    }
    async deleteDB(dbName, _account) {
        try {
            await Dexie.delete(dbName);
            this.closeSpecific(dbName, _account);
            return {
                success: true,
            };
        }
        catch (ex) {
            console.error('deleteDB-Error', ex);
            return {
                success: false,
            };
        }
    }
    initDb(name, _account = '') {
        var _a;
        if (!(name in this.schemas)) {
            throw new Error('db not defined');
        }
        let account = _account;
        if ((_a = this.schemas[name]) === null || _a === void 0 ? void 0 : _a.global) {
            account = 'global';
        }
        else if (!_account || !_account.length) {
            account = lodashGet(this.systemApi.getCurrentUser(), 'accountMd5', '');
        }
        if (typeof account === 'string' && account.includes('@')) {
            const tempAccount = this.accountApi.getEmailIdByEmail(_account);
            if (tempAccount) {
                account = this.systemApi.md5(account, true);
            }
        }
        if (!this.accountAction.has(account)) {
            this.accountAction.set(account, new ActionStore());
        }
        const action = this.accountAction.get(account);
        if (name in action.openedSchemas) {
            return action.openedSchemas[name];
        }
        action.openedSchemas[name] = this.createSchema(this.schemas[name], account);
        return action.openedSchemas[name];
    }
    createSchema(it, _accountMd5 = '') {
        let accountMd5 = _accountMd5;
        if (!(accountMd5 && accountMd5.length) && !it.global) {
            throw new Error('not login');
        }
        if (typeof accountMd5 === 'string' && accountMd5.includes('@')) {
            const standardAccount = this.accountApi.getEmailIdByEmail(_accountMd5);
            accountMd5 = this.systemApi.md5(standardAccount, true);
        }
        const dbName = it.global ? it.name : it.name + '_' + (accountMd5 || new Date().getTime());
        const db = new Dexie(dbName, { chromeTransactionDurability: 'relaxed' });
        if (it.versionchange && typeof it.versionchange === 'function') {
            db.on('versionchange', it.versionchange);
        }
        const schema = db.version(it.version);
        const tbs = {};
        it.tables.forEach(tb => {
            tbs[tb.name] = this.buildTableDefStr(tb);
            it.tableIndex = it.tableIndex || {};
            it.tableIndex[tb.name] = tb;
        });
        const version = schema.stores(tbs);
        if (it.upgrade) {
            version.upgrade(it.upgrade);
        }
        const dbTables = {};
        db.tables.forEach(table => {
            if (it.tableIndex && it.tableIndex[table.name]) {
                const tbDef = it.tableIndex[table.name];
                tbDef.model && table.mapToClass(tbDef.model);
            }
            dbTables[table.name] = table;
        });
        const ret = Object.assign(Object.assign({}, it), { instance: db, dbTableController: dbTables });
        console.log('[db] all tables:', db.tables.map(t => t.name));
        return ret;
    }
    buildTableDefStr(tb) {
        let ret = '';
        const prColumn = tb.primaryKey.columns.map((it) => {
            let r = '';
            if (typeof it === 'string') {
                r = it;
            }
            else {
                const item = it;
                r = item.autoIncrement ? '++' + item.name : item.name;
            }
            return r;
        });
        ret += prColumn.join(',');
        if (tb.index && tb.index.length > 0) {
            const indexColumn = tb.index.map((it) => {
                if (!it || !it.columns || it.columns.length === 0) {
                    return '';
                }
                let ret = '';
                if (it.columns.length === 1) {
                    const indexStr = it.multi ? '*' + it.columns[0] : it.columns[0];
                    ret = it.unique ? '&' + it.columns[0] : indexStr;
                }
                else {
                    ret = '[' + it.columns.join('+') + ']';
                }
                return ret;
            });
            ret += ',' + indexColumn.join(',');
        }
        return ret;
    }
    afterInit() {
        const user = this.systemApi.getCurrentUser();
        const userMd5 = (user === null || user === void 0 ? void 0 : user.accountMd5) || '';
        this.action.user = user;
        this.action.lock = this.storeApi.getLock(this.LOCK_KEY_FOR_DB);
        if (!this.accountAction.has(userMd5) && userMd5.length > 0) {
            this.accountAction.set(userMd5, new ActionStore());
        }
        return this.name;
    }
    afterLogin() {
        const user = this.systemApi.getCurrentUser();
        const userMd5 = (user === null || user === void 0 ? void 0 : user.accountMd5) || '';
        this.action.user = user;
        this.action.lock = this.storeApi.getLock(this.LOCK_KEY_FOR_DB);
        if (!this.accountAction.has(userMd5) && userMd5.length > 0) {
            this.accountAction.set(userMd5, new ActionStore());
        }
        return this.name;
    }
    beforeLogout() {
        this.close();
        this.action = new ActionStore();
        [...this.accountAction.keys()].forEach(account => {
            this.accountAction.set(account, new ActionStore());
        });
        return this.name;
    }
    useCache(config) {
        var _a;
        const { dbName } = config;
        return !!((_a = db_tables[dbName]) === null || _a === void 0 ? void 0 : _a.cache);
    }
    async innerGetByRangeCondition(query, _account) {
        const dbTable = this.testDbTableNotExist(query, _account);
        if (!dbTable) {
            return Promise.reject(new Error('请检查参数，不存在请求的数据库或表'));
        }
        try {
            let ret;
            let transFilter;
            if (query.adCondition) {
                if (query.adCondition.args && query.adCondition.field) {
                    const where = dbTable.where(query.adCondition.field);
                    let type = query.adCondition.type;
                    const { args, field } = query.adCondition;
                    const fieldCount = Array.isArray(field) ? field.length : 1;
                    if (type === 'anyOf' && fieldCount === 1 && args.length === 1 && Array.isArray(args[0]) && args[0].length > 10000) {
                        transFilter = {
                            key: field,
                            value: args[0],
                        };
                    }
                    else {
                        if (type === 'anyOf' && fieldCount > 1 && args.length === 1) {
                            type = 'equals';
                        }
                        const whereElement = where[type];
                        if (whereElement && typeof whereElement === 'function') {
                            ret = whereElement.apply(where, query.adCondition.args);
                        }
                    }
                }
            }
            if (!ret) {
                ret = dbTable.toCollection();
            }
            let totalCount = null;
            if (query && query.returnTotal) {
                totalCount = await this.getCollectionCount(ret);
            }
            const res = await this.handleQueryCollection(query, ret, _account);
            if (transFilter) {
                const valSet = new Set(transFilter.value);
                const key = Array.isArray(transFilter.key) ? transFilter.key[0] : transFilter.key;
                return res.filter(item => valSet.has(item[key]));
            }
            if (totalCount) {
                return {
                    totalCount,
                    data: res,
                };
            }
            return res;
        }
        catch (ex) {
            console.error('innerGetByRangeCondition error', ex);
            this.handleDexieCommonError(null, ex);
            return Promise.reject(ex);
        }
    }
    async getByRangeCondition(query, _account) {
        return this.innerGetByRangeCondition(query, _account);
    }
    getByRangeConditionWithTotalCount(query, _account) {
        query.returnTotal = true;
        return this.innerGetByRangeCondition(query, _account);
    }
    async getCollectionCount(coll) {
        try {
            if (coll) {
                const collCopied = coll.clone();
                return await collCopied.count();
            }
            return null;
        }
        catch (ex) {
            console.error('getCollectionCount error', ex);
            return null;
        }
    }
    runRangeQuery(query, dbTable) {
        let ret;
        if (query.adCondition) {
            if (query.adCondition.args && query.adCondition.field) {
                const where = dbTable.where(query.adCondition.field);
                const whereElement = where[query.adCondition.type];
                if (whereElement && typeof whereElement === 'function') {
                    ret = whereElement.apply(where, query.adCondition.args);
                }
            }
        }
        return ret;
    }
    async innerGetByEqCondition(query, _account) {
        const dbTable = this.testDbTableNotExist(query, _account);
        if (!dbTable) {
            return Promise.reject(new Error('请检查参数，不存在请求的数据库或表'));
        }
        try {
            let ret;
            if (query.order && query.orderUsingIndex) {
                ret = dbTable.orderBy(query.order);
            }
            else if (query.query) {
                ret = dbTable.where(query.query);
            }
            else {
                ret = dbTable.toCollection();
            }
            let totalCount = null;
            if (query && query.returnTotal) {
                totalCount = await this.getCollectionCount(ret);
            }
            const res = await this.handleQueryCollection(query, ret, _account);
            if (totalCount) {
                return {
                    totalCount,
                    data: res,
                };
            }
            return res;
        }
        catch (ex) {
            console.error('innerGetByEqCondition error', ex);
            this.handleDexieCommonError(null, ex);
            return Promise.reject(ex);
        }
    }
    showDeleteAllDataBaseTip() {
        if (hasShowConfim)
            return;
        this.dataTrackApi.track('SHOW_DELETE_ALL_DATABASE_TIP');
        const res = window.confirm('系统检测到您本地存在高版本缓存数据，页面部分功能可能受到影响，是否清空高版本缓存？');
        hasShowConfim = true;
        if (res) {
            this.deleteAllDataBases()
                .then(() => {
                this.dataTrackApi.track('USER_DELETE_ALL_DATABASE_SUCCESS');
                window.location.reload();
            })
                .catch(ex => {
                this.dataTrackApi.track('USER_DELETE_ALL_DATABASE_ERROR');
                console.error('deleteAllDataBases error', ex);
                window.location.reload();
            });
        }
    }
    async getByEqConditionWithTotal(query, _account) {
        query.returnTotal = true;
        return this.innerGetByEqCondition(query, _account);
    }
    async getByEqCondition(query, _account) {
        const res = (await this.innerGetByEqCondition(query, _account));
        return res;
    }
    async deleteByByRangeCondition(query, _account) {
        const dbTable = this.testDbTableNotExist(query, _account);
        if (!dbTable) {
            return Promise.reject(new Error('请检查参数，不存在请求的数据库或表'));
        }
        try {
            const ret = this.runRangeQuery(query, dbTable);
            if (!ret) {
                return Promise.reject(new Error('请检查查询条件，无法创建查询'));
            }
            return ret.delete();
        }
        catch (ex) {
            console.error('deleteByByRangeCondition-error', ex);
            this.handleDexieCommonError(null, ex);
            return Promise.reject(ex);
        }
    }
    addFilterRegistry(registry) {
        const action = this.accountAction.get('global');
        action.filterRegistry.set(registry.name, registry);
    }
    async handleQueryCollection(query, ret, _account) {
        var _a;
        const action = this.accountAction.get('global');
        const { useDexieFilter = true, filterLimit, additionalData, desc = true } = query;
        const count = query.count || (filterLimit ? 10000 : query.count);
        const configFilters = Array.isArray(query.filter) ? query.filter : query.filter ? [query.filter] : undefined;
        const filters = [];
        if (configFilters && configFilters.length > 0) {
            configFilters.forEach(it => {
                if (action.filterRegistry.has(it)) {
                    const queryFilterRegistry = action.filterRegistry.get(it);
                    if (queryFilterRegistry && queryFilterRegistry.filterFunc)
                        filters.push(queryFilterRegistry.filterFunc);
                }
            });
            if (useDexieFilter && filters.length > 0) {
                filters.forEach(it => {
                    ret = ret.filter(x => it(x, query));
                });
            }
        }
        if (query.start && query.start > 0) {
            ret.offset(query.start);
        }
        if (count && count > 0) {
            ret.limit(count);
        }
        if (!desc) {
            ret.reverse();
        }
        if (query.order && !query.orderUsingIndex) {
            return ret.toArray().then(res => {
                const key = query.order;
                return res.sort(getCommonCompareFunc(key));
            });
        }
        const res = await ret.toArray();
        if (!useDexieFilter && filters.length > 0) {
            const resData = (additionalData === null || additionalData === void 0 ? void 0 : additionalData.lastData) || [];
            try {
                res.forEach(x => {
                    const flag = filters.every(it => it(x, query));
                    if (flag) {
                        resData.push(x);
                    }
                    if (filterLimit && resData.length >= filterLimit) {
                        console.warn('[db] filterLimit break');
                        throw new Error();
                    }
                });
            }
            catch (e) {
                return resData;
            }
            if (filterLimit && res.length === count && resData.length < filterLimit) {
                console.warn('[db] filterLimit loop', resData.length);
                const newQuery = Object.assign({}, query);
                const field = (((_a = newQuery.adCondition) === null || _a === void 0 ? void 0 : _a.field) || 'id');
                newQuery.adCondition = {
                    type: 'above',
                    args: [res[res.length - 1][field]],
                    field,
                };
                newQuery.additionalData = Object.assign(Object.assign({}, (newQuery.additionalData || {})), { lastData: resData });
                return this.getByRangeCondition(newQuery, _account);
            }
            return resData;
        }
        return res;
    }
    getTableCount(db, _account) {
        const dbTable = this.testDbTableNotExist(db, _account);
        if (!dbTable) {
            return Promise.reject(new Error('请检查参数，不存在请求的数据库或表'));
        }
        return dbTable.count();
    }
    getById(db, id, _account) {
        const dbTable = this.testDbTableNotExist(db, _account);
        if (!dbTable) {
            return Promise.reject(new Error('请检查参数，不存在请求的数据库或表'));
        }
        return dbTable.get(id);
    }
    async getByIds(db, ids, _account) {
        const dbTable = this.testDbTableNotExist(db, _account);
        if (!dbTable) {
            return Promise.reject(new Error('请检查参数，不存在请求的数据库或表'));
        }
        const res = await dbTable.bulkGet(ids);
        return res;
    }
    async getByIndexIds(db, keyPath, ids, _account) {
        const dbTable = this.testDbTableNotExist(db, _account);
        if (!dbTable) {
            return Promise.reject(new Error('请检查参数，不存在请求的数据库或表'));
        }
        const res = await dbTable.where(keyPath).anyOf(ids).toArray();
        return res;
    }
    async deleteById(db, id, _account) {
        console.log('[db] ==== delete from db for data of db:', db);
        const dbTable = this.testDbTableNotExist(db, _account);
        if (!dbTable) {
            return Promise.reject(new Error('请检查参数，不存在请求的数据库或表'));
        }
        try {
            const idList = util.singleToList(id);
            return lodashChunk(idList, 2000).reduce((total, curIds, curIndex) => {
                total = total
                    .then(async () => {
                    if (curIndex !== 0) {
                        await this.dbWait(200);
                    }
                    return dbTable.bulkDelete(curIds);
                })
                    .catch(ex => {
                    console.error('[daxie_db_impl]deleteById1.error', ex);
                });
                return total;
            }, Promise.resolve());
        }
        catch (ex) {
            this.handleDexieCommonError(db, ex);
            return Promise.reject(ex);
        }
        finally {
            console.log('finally db');
        }
    }
    async put(db, items, _account) {
        console.log('[db] ==== insert into db for data of db:' + db.dbName + '.' + db.tableName, items);
        if (!items) {
            return items;
        }
        const dbTable = this.testDbTableNotExist(db, _account);
        if (!dbTable) {
            return Promise.reject(new Error('请检查参数，不存在请求的数据库或表'));
        }
        items = this.dbCloneItem(items);
        try {
            const res = await dbTable.put(items);
            if (res) {
                Object.defineProperty(items, '__prKey__', {
                    value: res,
                    writable: false,
                });
            }
            return items;
        }
        catch (ex) {
            this.handleDexieCommonError(db, ex);
            return Promise.reject(ex);
        }
        finally {
            console.log('finally db');
        }
    }
    async bulkPut(db, items, _account) {
        const dbTable = this.testDbTableNotExist(db, _account);
        if (!dbTable) {
            return Promise.reject(new Error('请检查参数，不存在请求的数据库或表'));
        }
        items = this.dbClone(items);
        if (db.dbName !== 'contact_dexie') {
            const keys = await dbTable.bulkPut(items, undefined, { allKeys: true });
            keys.forEach((val, idx) => {
                Object.defineProperty(items[idx], '__prKey__', {
                    value: val,
                    writable: false,
                });
            });
            return items;
        }
        const keys = await dbTable.bulkPut(items, undefined, { allKeys: true }).catch(Dexie.BulkError, e => {
            console.error(`[dexie]${items.length - e.failures.length}条数据写入成功.error:`, e);
        });
        keys.forEach((val, idx) => {
            Object.defineProperty(items[idx], '__prKey__', {
                value: val,
                writable: false,
            });
        });
        return items;
    }
    async putAll(db, items, _options, _account) {
        console.log(`[daxie_db_impl] putAll acount: ${_account}, dbName: ${db.dbName}, tableName: ${db.tableName} items.length: ${items.length}`);
        if (!items || items.length === 0) {
            return items;
        }
        const pageSize = lodashGet(_options, 'pageSize', 1000);
        try {
            const ret = await (items.length > pageSize ? this.putLargeDataSetToDb(db, items, _options, _account) : this.bulkPut(db, items, _account));
            if (items.length <= pageSize && (_options === null || _options === void 0 ? void 0 : _options.onProgress)) {
                try {
                    _options.onProgress({
                        tableName: db.tableName,
                        chunkIndex: 0,
                        chunkTotal: 1,
                        writtenSoFar: items.length,
                        totalItems: items.length,
                    });
                }
                catch (progressErr) {
                    console.warn('[daxie_db_impl] onProgress callback error:', progressErr);
                }
            }
            console.log(`[daxie_db_impl] put all finished acount: ${_account}, dbName: ${db.dbName}, tableName: ${db.tableName}, items.length: ${items.length}`);
            return ret;
        }
        catch (ex) {
            this.handleDexieCommonError(db, ex);
            return Promise.reject(ex);
        }
        finally {
            console.log('[daxie_db_impl] finally db');
        }
    }
    async putLargeDataSetToDb(db, items, _options, _account) {
        const pageSize = lodashGet(_options, 'pageSize', 1000);
        const intervalTime = lodashGet(_options, 'intervalTime', 1500);
        const onProgress = _options === null || _options === void 0 ? void 0 : _options.onProgress;
        const itemParams = lodashChunk(items, pageSize);
        const result = [];
        let requestPromise = Promise.resolve();
        itemParams.forEach((chunkItems, chunkIndex) => {
            requestPromise = requestPromise
                .then(async () => {
                if (chunkIndex !== 0) {
                    await this.dbWait(intervalTime);
                }
                await this.bulkPut(db, chunkItems, _account);
                result.push(...chunkItems);
                if (onProgress) {
                    try {
                        onProgress({
                            tableName: db.tableName,
                            chunkIndex,
                            chunkTotal: itemParams.length,
                            writtenSoFar: result.length,
                            totalItems: items.length,
                        });
                    }
                    catch (progressErr) {
                        console.warn('[daxie_db_impl] onProgress callback error:', progressErr);
                    }
                }
            })
                .catch(e => {
                console.warn('[db] batch put item error : ', e);
                throw e;
            });
        });
        await requestPromise;
        return result;
    }
    testDbTableNotExist(db, _account) {
        var _a;
        const { _dbAccount, _account: gc_account } = db;
        const standardAccount = this.accountApi.getEmailIdByEmail(_account || '');
        let account = standardAccount || (typeof _dbAccount === 'string' && _dbAccount.length ? _dbAccount : gc_account || '');
        if ((_a = this.schemas[db.dbName]) === null || _a === void 0 ? void 0 : _a.global) {
            account = 'global';
        }
        else if (!account || !account.length) {
            account = lodashGet(this.systemApi.getCurrentUser(), 'accountMd5', '');
        }
        if (typeof account === 'string' && account.includes('@')) {
            account = this.systemApi.md5(account, true);
        }
        if (!this.accountAction.has(account)) {
            this.accountAction.set(account, new ActionStore());
        }
        const action = this.accountAction.get(account);
        const isDbExist = lodashGet(action.openedSchemas, `[${db.dbName}].dbTableController[${db.tableName}].name.length`, 0) !== 0;
        if (!isDbExist) {
            this.initDb(db.dbName, account);
        }
        const dbInstance = action.openedSchemas[db.dbName].dbTableController[db.tableName];
        return dbInstance;
    }
    handleDexieCommonError(db, ex) {
        console.warn('[db] put all data error', db, ex);
        if (getIsVersionError(ex)) {
            this.showDeleteAllDataBaseTip();
        }
    }
    removeAccountAction(emailMD5) {
        if (this.accountAction.has(emailMD5)) {
            this.accountAction.delete(emailMD5);
        }
    }
    async clear(db, _account) {
        const dbTable = this.testDbTableNotExist(db, _account);
        await (dbTable === null || dbTable === void 0 ? void 0 : dbTable.clear());
        return true;
    }
    async deleteAllDataBases() {
        const dbNames = await this.getTotalDatabaseNames();
        await Promise.all(dbNames.map(dbName => {
            return this.deleteDB(dbName);
        }));
    }
    async getTotalDatabaseNames(filterDbName = false) {
        const dbNames = await Dexie.getDatabaseNames();
        if (filterDbName) {
            const whiteDbNameList = ['account', 'nim-', 'fileop_', 'storageDB'];
            return dbNames.filter(dbName => {
                const inWhiteList = whiteDbNameList.find(whiteDbName => dbName.indexOf(whiteDbName) === 0);
                if (inWhiteList) {
                    return false;
                }
                return true;
            });
        }
        return dbNames;
    }
}
const newDBApi = new DexieDbApiImpl();
api.registerLogicalApi(newDBApi);
export default newDBApi;
